import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUser } from 'react-icons/fa';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  AttentionMessage,
  Button,
  Container,
  Grid,
  TabPanel,
  TabPanels,
  Tabs,
  useThemedValueSwitch,
} from '@tq/ui-components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';

import { useConsultations } from 'api';
import {
  AssignModal,
  ConsultationCard,
  ConsultationCardLoading,
  DataTablePagination,
  MobileTabList,
  PageHeading,
} from 'components';
import { useAssignModal } from 'hooks';
import { useRecoilState } from 'recoil';
import {
  searchPageNumberState,
  searchPageSizeState,
  searchViewTypeState,
  searchViewTabIndexState,
} from 'store';
import { ConsultationViewTypes } from 'types';
import {
  ConsultationViewTypeMobile,
  SideFilter,
  StatisticsBar,
  TopFilter,
} from './components';

export const Search: React.FC = () => {
  const { tvs } = useThemedValueSwitch();
  const { t } = useTranslation('search');
  const { data: consultationsData, isLoading } = useConsultations();
  const tableRef = useRef(null);

  const isMobile = useIsMobile();
  const [viewType] = useRecoilState(searchViewTypeState);
  const [tabIndex, setTabIndex] = useRecoilState(searchViewTabIndexState);

  const {
    showAssignModal,
    showAssignButton,
    isConsultationHighlighted,
    handleAssignSelected,
    handleClose,
  } = useAssignModal();

  const content = (
    <Grid gridTemplateColumns={{ base: 'auto', lg: '349px auto' }} gridGap={8} width='100%'>
      <SideFilter
        isLoading={isLoading}
        display={{ base: 'none', lg: 'unset' }}
        px={5}
        py={8}
        borderRadius={5}
        border='1px solid '
        borderColor={tvs({ ni: COLOURS.gray[400], tq: 'grey.300' })}
        height='fit-content'
        sx={{ boxShadow: tvs({ ni: 'unset', tq: '0px 0px 12px -8px rgba(0,0,0,0.35)' }) }}
      />
      <Grid gridGap={{ base: 4, lg: 8 }} height='fit-content'>
        {showAssignButton && (
          <Button
            variant='secondary'
            justifySelf='left'
            rightIcon={<FaUser />}
            width={isMobile ? '100%' : 'inherit'}
            onClick={handleAssignSelected}
          >
            {t('assign_selected', 'Assign selected')}
          </Button>
        )}
        <Grid>
          {isLoading && <ConsultationCardLoading />}
          {!!consultationsData?.consultations?.items?.length && (
            <>
              <Grid
                gridGap={4}
                ref={tableRef}
                aria-label={t('consultations_list', 'Consultations list')}
                listStyleType='none'
                as='ul'
                role='list'
              >
                {consultationsData?.consultations?.items?.map((consultation) => (
                  <ConsultationCard
                    key={consultation.id}
                    consultation={consultation}
                    isHighlighted={isConsultationHighlighted(consultation.id)}
                  />
                ))}
              </Grid>
              {consultationsData.paginationInfo && (
                <DataTablePagination
                  paginationInfo={consultationsData.paginationInfo}
                  pageNumberState={searchPageNumberState}
                  pageSizeState={searchPageSizeState}
                  tableRef={tableRef}
                  tableScrollHeaderOffset={isMobile ? 80 : 120}
                />
              )}
            </>
          )}

          {!isLoading && !consultationsData?.consultations?.items?.length && (
            <AttentionMessage
              status='info'
              variant='inline'
              title={t(
                'no_search_results_message',
                'No results found for your search criteria. Try entering a different search term or adjusting the filters'
              )}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Container px={{ base: 0, lg: 4, xl: 0 }}>
      <Grid gridGap={{ base: 0, lg: 8 }} mb={8}>
        {viewType === ConsultationViewTypes.Open ? (
          <PageHeading
            heading={t('dashboard_title_open', 'Dashboard - Open consultations')}
            mobileHeading={t('dashboard_title_open_mobile', 'Open consultations')}
          />
        ) : (
          <PageHeading
            heading={t('dashboard_title_archived', 'Dashboard - Archived consultations')}
            mobileHeading={t('dashboard_title_archived_mobile', 'Archived consultations')}
          />
        )}
        {isMobile ? (
          <Grid gridGap={4}>
            <ConsultationViewTypeMobile />
            <Tabs index={tabIndex} variant='line' width='100vw' mx={-5} mb={12}>
              <MobileTabList
                activeTabIndex={tabIndex}
                onChangeActiveTab={setTabIndex}
                tabLabels={[
                  t('tabs_consultations', 'Consultations'),
                  t('tabs_statistics', 'Latest statistics'),
                ]}
              />
              <TabPanels>
                <TabPanel pt={0} px={5} width='100vw' left={0}>
                  <Grid gridGap={4}>
                    <TopFilter />
                    {content}
                  </Grid>
                </TabPanel>
                <TabPanel px={5}>
                  <StatisticsBar />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Grid>
        ) : (
          <>
            <StatisticsBar />
            <TopFilter />
            {content}
          </>
        )}
      </Grid>
      <AssignModal isOpen={showAssignModal} onClose={handleClose} />
    </Container>
  );
};
